import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Filter,
  History as HistoryIcon,
  Send,
} from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'

import { AppShell } from '@/components/layout/app-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { SelectSearch, type SelectSearchOption } from '@/components/ui/select-search'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { type PaginatedResponse } from '@/lib/api'

/**
 * Mission history.
 *
 * Backed by `GET /api/missions` (paginated, filterable). The
 * route is auto-scoped to the calling driver server-side, so we
 * don't pass a `driverId`.
 *
 * Filters
 * -------
 * Collapsible. Closed by default on mobile to keep the list
 * dense — the typical access pattern is "scroll back through my
 * recent bons", not "search for a specific one". When the driver
 * does open the panel, three controls cover ~all real-world
 * needs:
 *   - Date range (defaults: last 30 days).
 *   - Vehicle (`SelectSearch` over `/api/vehicles`).
 *   - Free text search on the site name.
 *
 * Pagination
 * ----------
 * "Load more" button at the bottom rather than infinite-scroll —
 * one less surprise for older drivers who aren't used to
 * infinite scroll, and it lets the cache key encode `page` so
 * every "Load more" tap caches its result for back-navigation.
 *
 * No detail modal — tapping a row navigates to `/missions/:id`.
 */

const PAGE_SIZE = 20

interface MissionRow {
  id: string
  site: string
  date: string
  driverName: string | null
  vehicleId: string | null
  vehicleRegistration: string | null
  clientId: string | null
  clientName: string | null
  totalMinutes: number
  nightMinutes: number
  verified: boolean
  readyToSend: boolean
}

interface VehicleLite {
  id: string
  registration: string
  brand: string | null
  model: string | null
  type: string | null
  active: boolean
}

export function MissionHistoryPage() {
  const api = useApi()
  const navigate = useNavigate()

  // Filters — committed values that drive the query, vs draft
  // values bound to the inputs. Submitting the filter form copies
  // draft → committed, which avoids one fetch per keystroke.
  const [draftDateFrom, setDraftDateFrom] = useState<string>(daysAgo(30))
  const [draftDateTo, setDraftDateTo] = useState<string>(today())
  const [draftVehicleId, setDraftVehicleId] = useState<string | null>(null)
  const [draftSearch, setDraftSearch] = useState<string>('')

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [committed, setCommitted] = useState({
    dateFrom: daysAgo(30),
    dateTo: today(),
    vehicleId: null as string | null,
    search: '',
  })

  const [page, setPage] = useState(1)

  // Reload from page 1 every time committed filters change.
  // Done implicitly: `page` is part of the query key, and
  // applyFilters resets it.
  const queryKey = useMemo(
    () =>
      [
        'missions',
        'history',
        committed.dateFrom,
        committed.dateTo,
        committed.vehicleId ?? '',
        committed.search,
        page,
      ] as const,
    [committed, page],
  )

  const historyQuery = useQuery({
    queryKey,
    queryFn: () => {
      const params: Record<string, string | number | boolean> = {
        page,
        limit: PAGE_SIZE,
        sortBy: 'date',
        sortOrder: 'desc',
        dateFrom: committed.dateFrom,
        dateTo: committed.dateTo,
      }
      if (committed.vehicleId !== null) params.vehicleId = committed.vehicleId
      if (committed.search.trim() !== '') params.search = committed.search.trim()
      return api.getPaginated<MissionRow>('/api/missions', params)
    },
    // Keep the previous page visible during pagination so the
    // user doesn't see a flash of empty list.
    placeholderData: (prev: PaginatedResponse<MissionRow> | undefined) => prev,
    staleTime: 60 * 1000,
  })

  // Vehicle list for the filter — same query as the form so
  // TanStack reuses the cache.
  const vehiclesQuery = useQuery({
    queryKey: ['vehicles', 'lite'] as const,
    queryFn: () =>
      api.get<VehicleLite[]>('/api/vehicles', {
        limit: 100,
        includeInactive: false,
        sortBy: 'registration',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  })

  const vehicleOptions: SelectSearchOption[] = useMemo(
    () =>
      (vehiclesQuery.data ?? []).map((v) => {
        const detailParts = [v.brand, v.model, v.type].filter(
          (s): s is string => s !== null && s !== '',
        )
        const opt: SelectSearchOption = { value: v.id, label: v.registration }
        if (detailParts.length > 0) opt.detail = detailParts.join(' · ')
        return opt
      }),
    [vehiclesQuery.data],
  )

  const rows = historyQuery.data?.data ?? []
  const total = historyQuery.data?.meta.total ?? 0
  const hasMore = page * PAGE_SIZE < total

  function applyFilters(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    setCommitted({
      dateFrom: draftDateFrom,
      dateTo: draftDateTo,
      vehicleId: draftVehicleId,
      search: draftSearch,
    })
    setPage(1)
  }

  function resetFilters(): void {
    const f = daysAgo(30)
    const t = today()
    setDraftDateFrom(f)
    setDraftDateTo(t)
    setDraftVehicleId(null)
    setDraftSearch('')
    setCommitted({ dateFrom: f, dateTo: t, vehicleId: null, search: '' })
    setPage(1)
  }

  return (
    <AppShell>
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="text-2xl font-bold text-text">Mes missions</h1>
        {total > 0 ? (
          <p className="text-sm text-text-muted">
            {total} résultat{total > 1 ? 's' : ''}
          </p>
        ) : null}
      </div>

      {/* Filters: collapsible — closed by default on mobile. */}
      <button
        type="button"
        onClick={() => setFiltersOpen((o) => !o)}
        aria-expanded={filtersOpen}
        className={[
          'w-full min-h-[3rem] px-4 mb-3 rounded-xl',
          'bg-surface border-2 border-surface-border',
          'flex items-center justify-between',
          'text-text font-medium',
          'active:bg-surface-muted transition-colors duration-100',
        ].join(' ')}
      >
        <span className="inline-flex items-center gap-2">
          <Filter size={18} aria-hidden="true" />
          Filtres
        </span>
        {filtersOpen ? (
          <ChevronUp size={18} aria-hidden="true" />
        ) : (
          <ChevronDown size={18} aria-hidden="true" />
        )}
      </button>

      {filtersOpen ? (
        <Card className="p-4 mb-4">
          <form onSubmit={applyFilters} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Du"
                type="date"
                value={draftDateFrom}
                onChange={(e) => setDraftDateFrom(e.target.value)}
              />
              <Input
                label="Au"
                type="date"
                value={draftDateTo}
                onChange={(e) => setDraftDateTo(e.target.value)}
              />
            </div>
            <SelectSearch
              label="Véhicule"
              placeholder="Tous les véhicules"
              value={draftVehicleId}
              onChange={setDraftVehicleId}
              options={vehicleOptions}
              loading={vehiclesQuery.isLoading}
            />
            <Input
              label="Recherche (chantier)"
              type="search"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              placeholder="Nom du chantier"
            />
            <div className="flex gap-2 pt-1">
              <Button type="submit" variant="primary" size="md" fullWidth>
                Appliquer
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={resetFilters}
              >
                Réinitialiser
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {/* List */}
      {historyQuery.isLoading ? (
        <div className="py-12 flex justify-center">
          <Spinner size={28} label="Chargement de l'historique" />
        </div>
      ) : historyQuery.isError ? (
        <Card className="p-4 text-danger">
          Impossible de charger l'historique. Vérifiez votre connexion.
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<HistoryIcon size={28} />}
          title="Aucune mission sur cette période"
          body="Modifiez les filtres ou élargissez la plage de dates."
        />
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((mission) => (
            <HistoryRow
              key={mission.id}
              mission={mission}
              onOpen={() =>
                void navigate({ to: '/missions/$id', params: { id: mission.id } })
              }
            />
          ))}
          {hasMore ? (
            <Button
              type="button"
              variant="secondary"
              fullWidth
              size="md"
              onClick={() => setPage((p) => p + 1)}
              loading={historyQuery.isFetching}
              disabled={historyQuery.isFetching}
            >
              Charger plus
            </Button>
          ) : (
            <p className="text-center text-sm text-text-muted py-2">
              Fin de l'historique
            </p>
          )}
        </div>
      )}
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  One mission row                                                            */
/* -------------------------------------------------------------------------- */

interface HistoryRowProps {
  mission: MissionRow
  onOpen: () => void
}

function HistoryRow({ mission, onOpen }: HistoryRowProps) {
  return (
    <Card interactive className="p-3 flex flex-col gap-1.5" onClick={onOpen}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-semibold text-text truncate">{mission.site}</p>
        <p className="text-xs text-text-muted shrink-0">{formatDate(mission.date)}</p>
      </div>

      <p className="text-sm text-text-muted truncate">
        {[mission.clientName, mission.vehicleRegistration]
          .filter((s) => s !== null && s !== '')
          .join(' · ') || '—'}
      </p>

      <div className="flex items-center justify-between gap-2 pt-0.5">
        <span className="text-sm font-mono tabular-nums">
          <strong>{formatMinutes(mission.totalMinutes)}</strong>
          {mission.nightMinutes > 0 ? (
            <span className="ml-1 text-xs text-text-muted">
              (nuit&nbsp;{formatMinutes(mission.nightMinutes)})
            </span>
          ) : null}
        </span>
        <div className="flex items-center gap-1">
          {mission.verified ? (
            <Badge tone="success">
              <CheckCircle2 size={12} aria-hidden="true" />
              Vérifié
            </Badge>
          ) : null}
          {mission.readyToSend && !mission.verified ? (
            <Badge tone="info">
              <Send size={12} aria-hidden="true" />
              Prêt
            </Badge>
          ) : null}
          {!mission.readyToSend && !mission.verified ? (
            <Badge tone="neutral">
              <ClipboardCheck size={12} aria-hidden="true" />
              Brouillon
            </Badge>
          ) : null}
        </div>
      </div>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function today(): string {
  const d = new Date()
  return formatLocalDate(d)
}

function daysAgo(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return formatLocalDate(d)
}

function formatLocalDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatDate(iso: string): string {
  // "2026-04-18" → "Sam 18 avr."
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1)
  return date
    .toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
    .replace(/\.$/, '')
}

function formatMinutes(total: number): string {
  if (total <= 0) return '0h00'
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  return `${hours}h${String(minutes).padStart(2, '0')}`
}
